if (!customElements.get('dobra-product-carousel')) {
  customElements.define('dobra-product-carousel', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.connectCarousel());
    }

    connectCarousel() {
      if (!this.isConnected || this.controller) return;
      this.slider = this.querySelector('slideshow-section');
      this.viewport = this.slider?.querySelector('[data-swiper]');
      this.rail = this.querySelector('[data-swiper-container]');
      this.controls = this.querySelector('[data-dobra-carousel-controls]');
      this.play = this.querySelector('[data-dobra-carousel-play]');
      const strings = this.querySelector('[data-dobra-carousel-strings]');
      if (!this.slider || !this.viewport || !this.rail || !this.controls || !this.play || !strings) return;
      this.strings = JSON.parse(strings.textContent);
      this.counter = this.querySelector('[data-dobra-carousel-counter]');
      this.state = this.querySelector('[data-dobra-carousel-state]');
      this.announcer = this.querySelector('[data-dobra-carousel-announcer]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches;
      this.resumeAt = 0;
      this.isVisible = false;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.play.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (!this.userPaused) this.resumeAt = 0;
        this.refreshPlayback();
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-dobra-carousel-prev], [data-dobra-carousel-next]')) {
          this.announceManualMove = true;
          this.deferPlayback();
        }
      }, { ...options, capture: true });
      this.rail.addEventListener('focusin', event => {
        const slide = event.target.closest('[data-swiper-slide]');
        if (this.swiper && slide && event.target.matches(':focus-visible')) {
          const index = Array.from(this.swiper.slides).indexOf(slide);
          const visible = Math.max(1, Math.floor(Number(this.swiper.params.slidesPerView) || 1));
          if (index < this.swiper.activeIndex || index >= this.swiper.activeIndex + visible) {
            this.swiper.slideTo(index, this.motion.matches ? 0 : 150);
          }
        }
        this.refreshPlayback();
      }, options);
      this.rail.addEventListener('focusout', () => queueMicrotask(() => {
        if (this.isConnected) this.refreshPlayback();
      }), options);
      this.rail.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) this.deferPlayback();
      }, { ...options, passive: true });
      document.addEventListener('visibilitychange', () => this.refreshPlayback(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.userPaused = true;
        this.refreshPlayback();
      }, options);
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.isVisible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.1;
        this.refreshPlayback();
      }, { threshold: [0, 0.1, 1] });
      this.visibilityObserver.observe(this.viewport);
      this.readyObserver = new MutationObserver(() => this.connectSwiper());
      this.readyObserver.observe(this.viewport, {
        attributes: true,
        attributeFilter: ['class']
      });
      this.connectSwiper();
    }

    connectSwiper() {
      const swiper = this.slider.swiper;
      if (!swiper || swiper.destroyed || swiper === this.swiper) return;
      this.disconnectSwiper();
      this.swiper = swiper;
      this.dataset.carouselEnhanced = 'true';
      this.onUpdate = () => {
        this.updatePosition();
        this.refreshPlayback();
      };
      this.onSlideChange = () => this.updatePosition();
      this.onTransitionEnd = () => {
        this.updatePosition();
        if (this.announceManualMove) {
          this.announcer.textContent = this.range;
          this.announceManualMove = false;
        }
      };
      this.onInteraction = () => this.deferPlayback();
      this.onTouchStart = () => {
        this.isInteracting = true;
        this.deferPlayback();
      };
      this.onTouchEnd = () => {
        this.isInteracting = false;
        this.deferPlayback();
      };
      this.onDestroy = () => this.disconnectSwiper();
      swiper.on('resize update lock unlock', this.onUpdate);
      swiper.on('slideChange', this.onSlideChange);
      swiper.on('transitionEnd', this.onTransitionEnd);
      swiper.on('sliderFirstMove', this.onInteraction);
      swiper.on('touchStart', this.onTouchStart);
      swiper.on('touchEnd', this.onTouchEnd);
      swiper.on('destroy', this.onDestroy);
      this.updatePosition();
      this.refreshPlayback();
    }

    updatePosition() {
      if (!this.swiper || this.swiper.destroyed) return;
      const swiper = this.swiper;
      const total = swiper.slides.length;
      this.controls.hidden = total === 0;
      this.controls.parentElement.hidden = total === 0;
      this.state.parentElement.hidden = total === 0;
      this.state.parentElement.parentElement.hidden = total === 0;
      const first = total ? Math.min(total, swiper.activeIndex + 1) : 0;
      const last = Math.min(total, first + Math.ceil(Number(swiper.params.slidesPerView) || 1) - 1);
      const locked = total < 2 || swiper.isLocked;
      this.querySelector('[data-dobra-carousel-prev]').disabled = locked || swiper.isBeginning;
      this.querySelector('[data-dobra-carousel-next]').disabled = locked || swiper.isEnd;
      this.play.disabled = locked;
      this.counter.textContent = total ? `${first}–${last} / ${total}` : '0 / 0';
      this.range = this.strings.range.replace('__first__', first).replace('__last__', last).replace('__total__', total);
      this.counter.setAttribute('aria-label', this.range);
    }

    deferPlayback() {
      this.resumeAt = Date.now() + 8000;
      this.refreshPlayback();
    }

    refreshPlayback() {
      clearTimeout(this.timer);
      if (!this.swiper || this.swiper.destroyed) return;
      const focusedProduct = this.rail.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
      const overflow = this.swiper.slides.length > 1 && !this.swiper.isLocked;
      const label = this.userPaused ? 'resume' : 'pause';
      this.play.querySelector('[data-dobra-carousel-play-label]').textContent = this.strings[label];
      this.play.querySelector('[data-dobra-carousel-symbol]').textContent = this.userPaused ? '▶' : 'Ⅱ';
      this.play.setAttribute('aria-label', this.strings[`${label}_label`]);
      this.swiper.params.speed = this.motion.matches ? 0 : 150;
      this.dataset.autoplayPaused = String(this.userPaused);
      let state = this.strings.automatic;
      if (!overflow) state = this.strings.all_visible;
      else if (this.userPaused) state = this.motion.matches ? this.strings.reduced_motion : this.strings.paused;
      else if (focusedProduct) state = this.strings.focused;
      else if (this.resumeAt > Date.now()) state = this.strings.deferred;
      this.state.textContent = state;
      this.rail.setAttribute('aria-live', this.userPaused ? 'polite' : 'off');
      if (this.userPaused || focusedProduct || this.isInteracting || !overflow || !this.isVisible || document.hidden) return;
      this.timer = setTimeout(() => {
        if (!this.swiper || this.swiper.destroyed) return;
        this.resumeAt = 0;
        this.swiper.slideTo(this.swiper.isEnd ? 0 : this.swiper.activeIndex + 1, this.motion.matches ? 0 : 150);
        this.updatePosition();
        this.refreshPlayback();
      }, Math.max(5000, this.resumeAt - Date.now()));
    }

    disconnectSwiper() {
      clearTimeout(this.timer);
      delete this.dataset.carouselEnhanced;
      if (!this.swiper) return;
      this.swiper.off('resize update lock unlock', this.onUpdate);
      this.swiper.off('slideChange', this.onSlideChange);
      this.swiper.off('transitionEnd', this.onTransitionEnd);
      this.swiper.off('sliderFirstMove', this.onInteraction);
      this.swiper.off('touchStart', this.onTouchStart);
      this.swiper.off('touchEnd', this.onTouchEnd);
      this.swiper.off('destroy', this.onDestroy);
      this.swiper = null;
      this.isInteracting = false;
      this.controls.hidden = true;
      this.state.parentElement.hidden = true;
    }

    disconnectedCallback() {
      this.disconnectSwiper();
      this.controller?.abort();
      this.visibilityObserver?.disconnect();
      this.readyObserver?.disconnect();
      this.controller = null;
    }
  });
}
